import { Stack, Step, Stepper, StepLabel, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import PageSection from 'components/common/PageSection'
import Icon from 'components/Icon'
import { CreateTestSuiteAddTests } from 'components/test-suites/forms/CreateTestSuiteAddTests'
import { CreateTestSuiteEditRisks } from 'components/test-suites/forms/CreateTestSuiteEditRisks'
import { CreateTestSuiteFormButtons } from 'components/test-suites/forms/CreateTestSuiteFormButtons'
import {
  CreateTestSuiteFormViewName,
  CreateTestSuiteFormViewItem
} from 'components/test-suites/forms/test-suite-form-utils'
import { TestSuiteAddTeams } from 'components/test-suites/forms/TestSuiteAddTeams'
import { useTestSuiteFormContext } from 'components/test-suites/forms/TestSuiteFormContext'
import { useGetTeams } from 'hooks/use-get-teams-paged'
import { useTranslation } from 'hooks/use-translation'

export const CreateTestSuiteForm: React.FC<{
  isSaving: boolean
  onHandleNextWithWarning: () => void
  shouldShowWarning: boolean
}> = ({ isSaving, onHandleNextWithWarning, shouldShowWarning }) => {
  const { testSuiteClone: testSuite } = useTestSuiteFormContext()

  const { trans } = useTranslation()

  const {
    data: teams,
    isFetching: isFetchingTeams,
    isError: hasErrorTeams
  } = useGetTeams()

  const { control, formState } = useFormContext()

  const isDisabled = useMemo(() => {
    const { isValid, isSubmitting } = formState

    return !isValid || isSubmitting
  }, [formState])

  const { errors } = formState

  const [name, scenarios] = useWatch({
    control,
    name: ['name', 'scenarios']
  })

  const hasNameOrScenariosErrors =
    !name ||
    !scenarios.length ||
    Boolean(errors.name) ||
    Boolean(errors.scenarios)

  const CREATE_TEST_SUITE_VIEW_LIST: CreateTestSuiteFormViewItem[] =
    useMemo(() => {
      return [
        {
          name: CreateTestSuiteFormViewName.AddTests,
          order: 0,
          isPrimaryButtonDisabled: hasNameOrScenariosErrors,
          primaryButtonText: trans('next'),
          primaryEndIcon: <Icon icon="arrowRight" />
        },
        {
          name: CreateTestSuiteFormViewName.EditRisks,
          order: 1,
          isPrimaryButtonDisabled: false, // no validation  because it is not required to edit risks
          primaryButtonText: trans('next'),
          primaryEndIcon: <Icon icon="arrowRight" />,
          secondaryButtonText: trans('back'),
          secondaryStartIcon: <Icon icon="arrowLeft" />
        },
        {
          name: CreateTestSuiteFormViewName.AddTeams,
          order: 2,
          isOptional: true,
          isPrimaryButtonDisabled: isDisabled,
          primaryButtonText: trans(
            'test_suite.forms.create_test_form.primary_button_text'
          ),
          secondaryButtonText: trans('back'),
          secondaryStartIcon: <Icon icon="arrowLeft" />
        }
      ]
    }, [hasNameOrScenariosErrors, isDisabled, trans])

  const steps = CREATE_TEST_SUITE_VIEW_LIST.map((view) => trans(view.name))
  const [activeStep, setActiveStep] = useState(0)
  const [activeView, setActiveView] = useState<CreateTestSuiteFormViewItem>(
    CREATE_TEST_SUITE_VIEW_LIST[0]
  )

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (shouldShowWarning && activeStep === steps.length - 1) {
      onHandleNextWithWarning()
      return
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1)
    setActiveView(CREATE_TEST_SUITE_VIEW_LIST[activeView.order + 1])
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
    setActiveView(CREATE_TEST_SUITE_VIEW_LIST[activeView.order - 1])
  }

  useEffect(() => {
    if (
      activeView.name === CreateTestSuiteFormViewName.AddTests &&
      activeView.isPrimaryButtonDisabled !== hasNameOrScenariosErrors
    ) {
      setActiveView(CREATE_TEST_SUITE_VIEW_LIST[0])
    }

    if (
      activeView.name === CreateTestSuiteFormViewName.AddTeams &&
      activeView.isPrimaryButtonDisabled !== isDisabled
    ) {
      setActiveView(CREATE_TEST_SUITE_VIEW_LIST[2])
    }
  }, [
    activeView.isPrimaryButtonDisabled,
    activeView.name,
    hasNameOrScenariosErrors,
    isDisabled,
    CREATE_TEST_SUITE_VIEW_LIST
  ])

  return (
    <>
      <Stepper activeStep={activeStep}>
        {steps.map((step, index) => {
          const labelProps: {
            optional?: React.ReactNode
          } = {}

          if (CREATE_TEST_SUITE_VIEW_LIST[index].isOptional) {
            labelProps.optional = (
              <Typography variant="caption">
                {trans(
                  'test_suite.forms.create_test_suite_form.stepper_optional'
                )}
              </Typography>
            )
          }

          return (
            <Step key={step}>
              <StepLabel {...labelProps}>{step}</StepLabel>
            </Step>
          )
        })}
      </Stepper>
      <PageSection
        errorMessage={
          <Typography>
            {trans('test_suites.errors.page_loading_error')}
          </Typography>
        }
        hasError={hasErrorTeams}
        isLoading={isFetchingTeams}
      >
        <Stack py={2} spacing={2}>
          {activeView.name === CreateTestSuiteFormViewName.AddTests && (
            <CreateTestSuiteAddTests teams={teams} />
          )}
          {activeView.name === CreateTestSuiteFormViewName.EditRisks && (
            <CreateTestSuiteEditRisks />
          )}
          {activeView.name === CreateTestSuiteFormViewName.AddTeams && (
            <TestSuiteAddTeams
              isDefaultTestSuite={Boolean(testSuite?.isDefault)}
              teams={teams}
            />
          )}
          <CreateTestSuiteFormButtons
            activeView={activeView}
            handleBack={handleBack}
            handleNext={handleNext}
            isSaving={isSaving}
            shouldShowWarning={shouldShowWarning}
          />
        </Stack>
      </PageSection>
    </>
  )
}
